/**
 * 性能监控系统
 * 用于测试和优化 Gongfu 各模块的性能
 */

import { EventEmitter } from 'events';
import * as os from 'os';
import { performance } from 'perf_hooks';
import { logger } from '../utils/logger';

/**
 * 性能指标
 */
export interface PerformanceMetrics {
  operation: string;
  duration: number;
  startTime: number;
  endTime: number;
  memoryUsage: NodeJS.MemoryUsage;
  cpuUsage: NodeJS.CpuUsage;
  metadata?: Record<string, any>;
}

/**
 * 性能报告
 */
export interface PerformanceReport {
  timestamp: string;
  systemInfo: SystemInfo;
  metrics: PerformanceMetrics[];
  summary: PerformanceSummary;
  recommendations: string[];
}

/**
 * 系统信息
 */
export interface SystemInfo {
  platform: string;
  cpus: number;
  totalMemory: number;
  freeMemory: number;
  nodeVersion: string;
}

/**
 * 性能摘要
 */
export interface PerformanceSummary {
  totalOperations: number;
  totalDuration: number;
  averageDuration: number;
  slowestOperation: PerformanceMetrics;
  fastestOperation: PerformanceMetrics;
  memoryPeakUsage: number;
  cpuPeakUsage: number;
}

/**
 * 性能监控器
 */
export class PerformanceMonitor extends EventEmitter {
  private metrics: PerformanceMetrics[] = [];
  private activeOperations: Map<string, {
    startTime: number;
    startMemory: NodeJS.MemoryUsage;
    startCpu: NodeJS.CpuUsage;
  }> = new Map();
  
  private readonly thresholds = {
    slowOperation: 1000,      // 1秒
    highMemoryUsage: 100,     // 100MB
    highCpuUsage: 80,         // 80%
  };
  
  constructor() {
    super();
  }
  
  /**
   * 开始监控操作
   */
  startOperation(operation: string, metadata?: Record<string, any>): void {
    const startTime = performance.now();
    const startMemory = process.memoryUsage();
    const startCpu = process.cpuUsage();
    
    this.activeOperations.set(operation, {
      startTime,
      startMemory,
      startCpu
    });
    
    logger.debug(`Performance: Started monitoring ${operation}`);
  }
  
  /**
   * 结束监控操作
   */
  endOperation(operation: string, metadata?: Record<string, any>): PerformanceMetrics | null {
    const opData = this.activeOperations.get(operation);
    if (!opData) {
      logger.warn(`Performance: No active operation found for ${operation}`);
      return null;
    }
    
    const endTime = performance.now();
    const endMemory = process.memoryUsage();
    const endCpu = process.cpuUsage(opData.startCpu);
    
    const duration = endTime - opData.startTime;
    
    const metrics: PerformanceMetrics = {
      operation,
      duration,
      startTime: opData.startTime,
      endTime,
      memoryUsage: {
        rss: endMemory.rss - opData.startMemory.rss,
        heapTotal: endMemory.heapTotal - opData.startMemory.heapTotal,
        heapUsed: endMemory.heapUsed - opData.startMemory.heapUsed,
        external: endMemory.external - opData.startMemory.external,
        arrayBuffers: endMemory.arrayBuffers - opData.startMemory.arrayBuffers
      },
      cpuUsage: endCpu,
      metadata
    };
    
    this.metrics.push(metrics);
    this.activeOperations.delete(operation);
    
    // 检查性能问题
    this.checkPerformanceIssues(metrics);
    
    // 发送事件
    this.emit('operation:complete', metrics);
    
    logger.debug(`Performance: ${operation} completed in ${duration.toFixed(2)}ms`);
    
    return metrics;
  }
  
  /**
   * 测量异步操作
   */
  async measure<T>(
    operation: string,
    fn: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    this.startOperation(operation, metadata);
    try {
      const result = await fn();
      this.endOperation(operation, metadata);
      return result;
    } catch (error) {
      this.endOperation(operation, { ...metadata, error: true });
      throw error;
    }
  }
  
  /**
   * 测量同步操作
   */
  measureSync<T>(
    operation: string,
    fn: () => T,
    metadata?: Record<string, any>
  ): T {
    this.startOperation(operation, metadata);
    try {
      const result = fn();
      this.endOperation(operation, metadata);
      return result;
    } catch (error) {
      this.endOperation(operation, { ...metadata, error: true });
      throw error;
    }
  }
  
  /**
   * 检查性能问题
   */
  private checkPerformanceIssues(metrics: PerformanceMetrics): void {
    const issues: string[] = [];
    
    // 检查慢操作
    if (metrics.duration > this.thresholds.slowOperation) {
      issues.push(`Slow operation: ${metrics.operation} took ${metrics.duration.toFixed(2)}ms`);
    }
    
    // 检查内存使用
    const memoryUsageMB = metrics.memoryUsage.heapUsed / 1024 / 1024;
    if (memoryUsageMB > this.thresholds.highMemoryUsage) {
      issues.push(`High memory usage: ${metrics.operation} used ${memoryUsageMB.toFixed(2)}MB`);
    }
    
    // 检查 CPU 使用
    const cpuUsagePercent = (metrics.cpuUsage.user + metrics.cpuUsage.system) / 1000000 * 100;
    if (cpuUsagePercent > this.thresholds.highCpuUsage) {
      issues.push(`High CPU usage: ${metrics.operation} used ${cpuUsagePercent.toFixed(2)}%`);
    }
    
    if (issues.length > 0) {
      this.emit('performance:issue', { metrics, issues });
      issues.forEach(issue => logger.warn(`Performance: ${issue}`));
    }
  }
  
  /**
   * 生成性能报告
   */
  generateReport(): PerformanceReport {
    const systemInfo = this.getSystemInfo();
    const summary = this.calculateSummary();
    const recommendations = this.generateRecommendations();
    
    return {
      timestamp: new Date().toISOString(),
      systemInfo,
      metrics: this.metrics,
      summary,
      recommendations
    };
  }
  
  /**
   * 获取系统信息
   */
  private getSystemInfo(): SystemInfo {
    return {
      platform: os.platform(),
      cpus: os.cpus().length,
      totalMemory: os.totalmem(),
      freeMemory: os.freemem(),
      nodeVersion: process.version
    };
  }
  
  /**
   * 计算性能摘要
   */
  private calculateSummary(): PerformanceSummary {
    if (this.metrics.length === 0) {
      return {
        totalOperations: 0,
        totalDuration: 0,
        averageDuration: 0,
        slowestOperation: null as any,
        fastestOperation: null as any,
        memoryPeakUsage: 0,
        cpuPeakUsage: 0
      };
    }
    
    const totalDuration = this.metrics.reduce((sum, m) => sum + m.duration, 0);
    const averageDuration = totalDuration / this.metrics.length;
    
    const sortedByDuration = [...this.metrics].sort((a, b) => b.duration - a.duration);
    const slowestOperation = sortedByDuration[0];
    const fastestOperation = sortedByDuration[sortedByDuration.length - 1];
    
    const memoryPeakUsage = Math.max(...this.metrics.map(m => m.memoryUsage.heapUsed));
    const cpuPeakUsage = Math.max(...this.metrics.map(m => 
      (m.cpuUsage.user + m.cpuUsage.system) / 1000000 * 100
    ));
    
    return {
      totalOperations: this.metrics.length,
      totalDuration,
      averageDuration,
      slowestOperation,
      fastestOperation,
      memoryPeakUsage,
      cpuPeakUsage
    };
  }
  
  /**
   * 生成优化建议
   */
  private generateRecommendations(): string[] {
    const recommendations: string[] = [];
    const summary = this.calculateSummary();
    
    // 慢操作建议
    const slowOps = this.metrics.filter(m => m.duration > this.thresholds.slowOperation);
    if (slowOps.length > 0) {
      recommendations.push(
        `优化慢操作：${slowOps.map(m => m.operation).join(', ')} 超过了 ${this.thresholds.slowOperation}ms`
      );
    }
    
    // 内存使用建议
    if (summary.memoryPeakUsage > this.thresholds.highMemoryUsage * 1024 * 1024) {
      recommendations.push(
        `减少内存使用：峰值内存使用达到 ${(summary.memoryPeakUsage / 1024 / 1024).toFixed(2)}MB`
      );
    }
    
    // CPU 使用建议
    if (summary.cpuPeakUsage > this.thresholds.highCpuUsage) {
      recommendations.push(
        `优化 CPU 密集操作：峰值 CPU 使用达到 ${summary.cpuPeakUsage.toFixed(2)}%`
      );
    }
    
    // 并发建议
    const concurrentOps = this.findConcurrentOperations();
    if (concurrentOps.length > 5) {
      recommendations.push(
        `考虑限制并发：检测到 ${concurrentOps.length} 个并发操作`
      );
    }
    
    return recommendations;
  }
  
  /**
   * 查找并发操作
   */
  private findConcurrentOperations(): PerformanceMetrics[] {
    const concurrent: PerformanceMetrics[] = [];
    
    for (let i = 0; i < this.metrics.length; i++) {
      for (let j = i + 1; j < this.metrics.length; j++) {
        const a = this.metrics[i];
        const b = this.metrics[j];
        
        // 检查时间重叠
        if (a.startTime < b.endTime && b.startTime < a.endTime) {
          if (!concurrent.includes(a)) concurrent.push(a);
          if (!concurrent.includes(b)) concurrent.push(b);
        }
      }
    }
    
    return concurrent;
  }
  
  /**
   * 清除指标
   */
  clear(): void {
    this.metrics = [];
    this.activeOperations.clear();
  }
  
  /**
   * 导出指标
   */
  exportMetrics(): PerformanceMetrics[] {
    return [...this.metrics];
  }
  
  /**
   * 设置阈值
   */
  setThresholds(thresholds: Partial<typeof this.thresholds>): void {
    Object.assign(this.thresholds, thresholds);
  }
}

/**
 * 全局性能监控实例
 */
export const performanceMonitor = new PerformanceMonitor();

/**
 * 性能装饰器
 */
export function measurePerformance(operation?: string) {
  return function (target: any, propertyKey: string, descriptor: PropertyDescriptor) {
    const originalMethod = descriptor.value;
    const operationName = operation || `${target.constructor.name}.${propertyKey}`;
    
    descriptor.value = async function (...args: any[]) {
      return performanceMonitor.measure(
        operationName,
        () => originalMethod.apply(this, args)
      );
    };
    
    return descriptor;
  };
}